import React from "react";
import _ from "lodash";
import { useDispatch } from "react-redux";
import {
  portfolioTransactionAmount,
  transactionValueToEuro,
  transactionValueToQuantity,
} from "../../../../../utils";
import {TRANSACTION_INSTR, TRANSACTION_TYPE_VALUES} from "../../../../../constants";
import {MODEL_PORTFOLIO_WEIGHT} from "../../../../../../Modelportfolios/Create/constants";
import {ModelPortfolioResource, parseResponse} from "../../../../../../../utils/api";
import {displayErrorSnackBar} from "../../../../../../../components/SnackbarProvider/actions";

const CHANGE_EVENT_FIELD_TYPE = {
  AMOUNT: 1,
  WEIGHT: 2,
  TOTAL: 3
}

/**
 * Hook for new virtual portfolio (model portfolio) creation logic (BCA-8464).
 * Manages weight distribution and recalculation of transaction values.
 */
const useNewVirtualPortfolio = ({portfolio, isVirtual, isNewVirtual, handleChange, handlePortfolioChange}) => {
  const dispatch = useDispatch();

  const [isWeightLoading, setIsWeightLoading] = React.useState(false);

  const newVirtualRecalculateTransaction = (portfolio, transaction, eventType, weight) => {
    eventType = eventType || CHANGE_EVENT_FIELD_TYPE.AMOUNT;

    const totalValue = portfolio.portfolioValueTotal;
    let transactionValue = transaction.transaction_value || 0;
    let transactionWeight = weight || _.get(transaction, 'calculated.transaction_value_weight');
    let transactionValueEUR;

    if (eventType === CHANGE_EVENT_FIELD_TYPE.AMOUNT) {
      transactionValueEUR = transactionValueToEuro(transaction.transaction_value, transaction) || 0;
      transactionWeight = !!totalValue ? _.round(transactionValueEUR / totalValue * 100, 2) : 0;
    } else {
      if (eventType == CHANGE_EVENT_FIELD_TYPE.TOTAL) {
        transactionValueEUR = _.round(transactionWeight * totalValue / 100, 2);
      } else {
        transactionValueEUR = totalValue * transactionWeight / 100;
      }

      transactionValue = transaction.transaction_type === TRANSACTION_INSTR.amount ? transactionValueEUR : transactionValueToQuantity(transactionValueEUR, transaction, false);
    }

    _.set(portfolio, 'trading_errors.portfolio', undefined);

    handleChange(transaction, {
      transaction_value: transactionValue,
      calculated: {
        ..._.get(transaction, 'calculated', {}),
        transaction_value_weight: transactionWeight
      },
      errors: {...transaction.errors, transaction_value: null, transaction_value_weight: null}
    });
  }

  const handleNewVirtualMissingTransactionAmount = (portfolio) => {
    if (![MODEL_PORTFOLIO_WEIGHT.EQUAL, MODEL_PORTFOLIO_WEIGHT.RISK_BASED].includes(portfolio.transactionsWeightType)) {
      return;
    }

    const totalTransactionsAmount = _.round(portfolioTransactionAmount(portfolio, TRANSACTION_TYPE_VALUES.BUY), 2);
    const totalDiff = _.round(portfolio.portfolioValueTotal - totalTransactionsAmount, 2);

    if (totalDiff) {
      let transactionAmount = _.get(portfolio, 'transactions.buy.0.transaction_value');
      transactionAmount = _.round(transactionAmount + totalDiff, 2);
      _.set(portfolio, 'transactions.buy.0.transaction_value', transactionAmount);
    }
  }

  const newVirtualRecalculateTransactions = (portfolio, eventType, weight) => {
    eventType = eventType || CHANGE_EVENT_FIELD_TYPE.AMOUNT;

    if (!isVirtual || !isNewVirtual) {
      return;
    }

    portfolio.transactions.buy.forEach((transaction) => {
      newVirtualRecalculateTransaction(portfolio, transaction, eventType, weight);
    });
    handleNewVirtualMissingTransactionAmount(portfolio);
  }

  const handleNewVirtualPortfolioValueTotalChange = (value) => {
    portfolio.portfolioValueTotal = value;
    newVirtualRecalculateTransactions(portfolio, CHANGE_EVENT_FIELD_TYPE.TOTAL);
  }

  const handleNewVirtualTransactionWeightChanged = (item, value) => {
    _.set(item, 'calculated.transaction_value_weight', value);
    newVirtualRecalculateTransaction(portfolio, item, CHANGE_EVENT_FIELD_TYPE.WEIGHT);
  }

  const handleNewVirtualTransactionWeightTypeChanged = async (value, triggerChange) => {
    if (typeof triggerChange === 'undefined') triggerChange = true;

    portfolio.transactionsWeightType = value;

    if (!_.isEmpty(portfolio.transactions.buy) && [MODEL_PORTFOLIO_WEIGHT.EQUAL, MODEL_PORTFOLIO_WEIGHT.RISK_BASED].includes(value)) {
      if (value === MODEL_PORTFOLIO_WEIGHT.EQUAL) {
        const weight = 100 / portfolio.transactions.buy.length;
        newVirtualRecalculateTransactions(portfolio, CHANGE_EVENT_FIELD_TYPE.WEIGHT, weight);
      } else {
        setIsWeightLoading(true);
        try {
          const isins = portfolio.transactions.buy.map((transaction) => transaction.data.isin);
          const response = await ModelPortfolioResource.getRiskParityWeights({instruments: isins});

          parseResponse(response, 'weights', (data) => {
            const weights = (data.components || []).reduce((result, component) => {
              result[component.isin] = (component.weight || 0) * 100;
              return result
            }, {});
            portfolio.transactions.buy.forEach((transaction) => {
              const weight = weights[transaction.data.isin] || 0;
              _.set(transaction, 'calculated.transaction_value_weight', weight);
              newVirtualRecalculateTransaction(portfolio, transaction, CHANGE_EVENT_FIELD_TYPE.WEIGHT)
            });
            handleNewVirtualMissingTransactionAmount(portfolio);
          }, (error) => {
            dispatch(displayErrorSnackBar('Gewichtung konnte nicht berechnet werden.'))
          })
        } catch (error) {
          dispatch(displayErrorSnackBar('Gewichtung konnte nicht berechnet werden.'))
        } finally {
          setIsWeightLoading(false);
        }
      }
    }

    _.set(portfolio, 'trading_errors.portfolio', undefined);

    triggerChange && handlePortfolioChange && handlePortfolioChange();
  }

  return {
    isWeightLoading,
    newVirtualRecalculateTransactions,
    handleNewVirtualPortfolioValueTotalChange,
    handleNewVirtualTransactionWeightChanged,
    handleNewVirtualTransactionWeightTypeChanged,
  };
}

export default useNewVirtualPortfolio;
